import { observer } from "mobx-react";
import React from "react";
import {
    EvalLicensedModule,
    EvalQuestion,
} from "components/Evaluation/Stores/EvalStore";
import Question, {
    QuestionDependencyType,
} from "models/Question";
import { Answer } from "models/Answer";
import { ThemeColors } from "Theme/AppTheme";
import { AnswerTypeNames } from "models/AnswerType";
import hexToRGB from "utils/hexToRGB";
import { buildQuestionVisibilityMap } from "utils/questionDependencyRendering";
import WFRScaledResponseAnswer from "./WFRScaledResponseAnswer";
import WFRStarRatingAnswer from "./WFRStarRatingAnswer";
import WFRTagOrFillInResponseAnswer from "./WFRTagOrFillResponseAnswer";
import { useStore } from "utils/useStore";
import { WorkflowReviewStore } from "components/WorkflowReview/Stores/WorkflowReviewStore";
import { WorkflowConditionType } from "components/Admin/Organizations/OrganizationDetail/OrganizationWorkflows/Types/WorkflowModels";
import { isNotNull } from "utils/helpers";

interface WFRQuestionAnswerListProps {
    module?: EvalLicensedModule;
    disputeHasStarted: boolean;
    standalone?: boolean;
    isEditable?: boolean;
    isNoteEditable?: boolean;
    hideHiddenAnswers?: boolean;
    isWorkflowModule?: boolean;
}

function canRenderWorkflowQuestion(
    answers: Answer[],
    question: EvalQuestion,
    workflowConditionType?: WorkflowConditionType,
    hiddenAnswers?: Answer[],
    isWorkflowModule?: boolean,
): boolean {
    //Do not render question if all are true:
    //Question is from the Evaluation Module (Top card of the WF reviewier)
    //There are no answers for this question
    //This is not a score escalation workflow (We want to show the entire module for SE workflows)
    if (
        !isWorkflowModule &&
        answers &&
        answers.filter((answer) => answer.questionId === question.id).length ===
            0 &&
        workflowConditionType !== WorkflowConditionType.ScoreEscalation
    ) {
        return false;
    }

    //For APT we only want to show the trigger question, all others are "hidden"
    //For Dispute we only want to show disputed questions, all others are "hidden"
    const hiddenAnswerQuestionIds =
        hiddenAnswers?.map(({ questionId }) => questionId) ?? [];

    if (hiddenAnswerQuestionIds.includes(question.id)) {
        return false;
    }

    return true;
}

export function buildWorkflowReviewQuestionVisibilityMap(
    questions: EvalQuestion[],
    answers: Answer[],
    workflowConditionType?: WorkflowConditionType,
    hiddenAnswers?: Answer[],
    isWorkflowModule?: boolean,
): Map<string, boolean> {
    return buildQuestionVisibilityMap({
        questions,
        answers,
        getModuleInstanceIdForQuestion: (question) =>
            question.evaluationModuleId,
        getModuleInstanceIdForAnswer: (answer) => answer.evaluationModuleId,
        canRenderQuestion: (question) =>
            canRenderWorkflowQuestion(
                answers,
                question,
                workflowConditionType,
                hiddenAnswers,
                isWorkflowModule,
            ),
    });
}

const WFRQuestionAnswerList: React.FC<WFRQuestionAnswerListProps> = observer(
    ({ isNoteEditable = true, ...props }) => {
        const store = useStore(WorkflowReviewStore);
        const questionsWithDependencies = props.module?.questions.filter(
            (r) => r.dependencies?.conditions.length,
        );

        const licensedModule = store.licensedModules.find(
            (module) => module.id === props.module?.id,
        );

        const hidden =
            store.workflowReviewerInfo?.workflowType ===
                WorkflowConditionType.APT && props.hideHiddenAnswers
                ? store.workflowReviewerInfo?.workflowInfo.hiddenAnswers
                : store.workflowReviewerInfo?.workflowType ===
                  WorkflowConditionType.EvaluationDispute
                ? store.workflowReviewerInfo?.workflowInfo.answers.filter(
                      (answer) =>
                          store.workflowReviewerInfo?.workflowType ===
                              WorkflowConditionType.EvaluationDispute &&
                          !store.workflowReviewerInfo.workflowInfo?.answerDisputes.some(
                              (dispute) => dispute.answerId === answer.id,
                          ),
                  )
                : [];

        const answersForModule = store.answers.filter(
            (answer) =>
                answer.evaluationModuleId ===
                (props.module?.evaluationModuleId as string),
        );

        const moduleQuestions = (props.module?.sortedQuestion ?? []).map((q) => {
            var evalQuestion = q as EvalQuestion;
            evalQuestion.evaluationModuleId = props.module?.evaluationModuleId as string;
            return evalQuestion;
        });
        const visibilityMap = buildWorkflowReviewQuestionVisibilityMap(
            moduleQuestions,
            answersForModule,
            store.workflowReviewerInfo?.workflowType,
            hidden,
            licensedModule?.isWorkflowModule && props.isWorkflowModule,
        );
        const triggerQuestions = moduleQuestions.filter(
            (question) => visibilityMap.get(question.id) ?? false,
        );
        const getQuestionsWithDependencies = (questions: Question[]) => {
            const answersQuestionsMap =
                questions.map((question) => ({
                    questionId: question.id,
                    value: store.answers
                        .find((answer) => answer.questionId === question.id)
                        ?.answerTags.map(
                            (answerTag) => answerTag.tag?.value,
                        )[0],
                })) ?? [];

            const childDeps =
                questionsWithDependencies?.filter((q) => {
                    return q.dependencies?.conditions.some((condition) => {
                        return !!answersQuestionsMap.find(
                            ({ questionId, value }) =>
                                condition.type ===
                                    QuestionDependencyType.QuestionAnswer &&
                                condition.dependsOnQuestionId === questionId &&
                                condition.value === value,
                        );
                    });
                }) ?? [];

            const parentDeps = questions
                .flatMap((q) =>
                    q.dependencies?.conditions
                        .map((condition) =>
                            (props.module?.sortedQuestion ?? []).find(
                                (question) =>
                                    condition.type ===
                                        QuestionDependencyType.QuestionAnswer &&
                                    condition.dependsOnQuestionId ===
                                        question.id,
                            ),
                        )
                        .filter(isNotNull),
                )
                .filter(isNotNull);

            return [...questions, ...childDeps, ...parentDeps].sort((a, b) => {
                if (a.order < b.order) {
                    return -1;
                }
                if (a.order > b.order) {
                    return 1;
                }
                return 0;
            });
        };

        const sortedQuestions =
            !props.isWorkflowModule &&
            store.workflowReviewerInfo?.workflowType ===
                WorkflowConditionType.APT
                ? getQuestionsWithDependencies(triggerQuestions)
                : triggerQuestions;

        if (!props.isWorkflowModule) {
            store.visibleQuestionIds = sortedQuestions.map(
                (question) => question.id,
            );
        }

        const moduleUIModel = store.moduleUIModels.get(
            props.module?.evaluationModuleId ?? "",
        );

        return props.module?.questions ? (
            <>
                {sortedQuestions?.map((question) => {
                    const answer = store.getAnswerForQuestion(
                        question,
                        moduleUIModel?.licensedModule.evaluationModuleId,
                    );

                    if (question.isActive) {
                        const leftBorder =
                            question.parentId &&
                            questionsWithDependencies?.includes(question)
                                ? hexToRGB(ThemeColors.purple, 1)
                                : questionsWithDependencies?.includes(question)
                                ? hexToRGB(ThemeColors.red, 0.75)
                                : question.parentId
                                ? hexToRGB(ThemeColors.blue, 0.5)
                                : undefined;

                        if (
                            question.answerType.answerTypeName ===
                                AnswerTypeNames.ScaledResponse ||
                            question.answerType.answerTypeName ===
                                AnswerTypeNames.ScaledBooleanResponse ||
                            question.answerType.answerTypeName ===
                                AnswerTypeNames.QuestionGrouping
                        ) {
                            return (
                                <WFRScaledResponseAnswer
                                    moduleUIModel={moduleUIModel}
                                    answerDisputes={answer?.answerDisputes}
                                    question={question}
                                    standalone={props.standalone}
                                    leftBorderColor={leftBorder}
                                    key={question.id}
                                    isDisabled={!props.isEditable}
                                    isNoteDisabled={!isNoteEditable}
                                    showDisputeCheckbox={
                                        props.disputeHasStarted
                                    }
                                    disableErrorHighlighting
                                />
                            );
                        } else if (
                            question.answerType.answerTypeName ===
                                AnswerTypeNames.TagResponse ||
                            question.answerType.answerTypeName ===
                                AnswerTypeNames.ScoredTagResponse ||
                            question.answerType.answerTypeName ===
                                AnswerTypeNames.TextResponse ||
                            question.answerType.answerTypeName ===
                                AnswerTypeNames.DateResponse
                        ) {
                            return (
                                <WFRTagOrFillInResponseAnswer
                                    key={question.id}
                                    standalone={props.standalone}
                                    leftBorderColor={leftBorder}
                                    moduleUIModel={moduleUIModel}
                                    question={question}
                                    isDisabled={!props.isEditable}
                                    isNoteDisabled={!isNoteEditable}
                                />
                            );
                        } else if (
                            question.answerType.answerTypeName ===
                            AnswerTypeNames.StarRating
                        ) {
                            return (
                                <WFRStarRatingAnswer
                                    key={question.id}
                                    standalone={props.standalone}
                                    moduleUIModel={moduleUIModel}
                                    question={question}
                                    leftBorderColor={leftBorder}
                                    isDisabled={!props.isEditable}
                                    showDisputeCheckbox={
                                        props.disputeHasStarted
                                    }
                                />
                            );
                        } else {
                            return "";
                        }
                    } else {
                        return "";
                    }
                })}
            </>
        ) : null;
    },
);

export default WFRQuestionAnswerList;
